import { useState } from 'react'

import type { Resource } from '../../api/client.ts'
import {
  DELIVERY_EXTERNAL_ID_PATTERN,
  type AttemptStatusFilter,
  type WebhookEndpointConfig,
} from '../../api/webhooks.ts'

import { AttemptsCard } from './AttemptsCard.tsx'
import { EndpointsCard } from './EndpointsCard.tsx'
import { DeadEndpointNotice, SummaryStats } from './SummaryStats.tsx'
import { variantsOf } from './types.ts'
import { useDebouncedValue } from './useDebouncedValue.ts'
import { useWebhookAttempts, useWebhookSummary } from './useWebhookData.ts'

// Everything the dashboard shows for delivery webhooks — split out of
// Webhooks.tsx so the type selector there can swap it for PayloadWebhooksPane
// without either pane knowing the other exists.

const DELIVERY_VARIANTS = variantsOf('deliveries')

const deliveryEndpoints = (
  resource: Resource<WebhookEndpointConfig[]>
): Resource<WebhookEndpointConfig[]> =>
  resource.status === 'ready'
    ? {
        ...resource,
        data: resource.data.filter(endpoint =>
          DELIVERY_VARIANTS.includes(endpoint.variant)
        ),
      }
    : resource

/** The filter box's text as the id the feed can be pinned to, or nothing while
 *  it is empty or still a partial id. */
const completeDeliveryId = (query: string): string | undefined => {
  const trimmed = query.trim()
  return DELIVERY_EXTERNAL_ID_PATTERN.test(trimmed) ? trimmed : undefined
}

export const DeliveryWebhooksPane = ({
  canEdit,
  config,
  windowDays,
  onWindowChange,
}: {
  canEdit: boolean
  config: { resource: Resource<WebhookEndpointConfig[]>; refresh: () => void }
  windowDays: number
  onWindowChange: (next: number) => void
}) => {
  const [status, setStatus] = useState<AttemptStatusFilter>('all')
  const [deliveryQuery, setDeliveryQuery] = useState('')
  // Debounced so a pasted or typed id costs one read, not one per keystroke.
  const deliveryExternalId = useDebouncedValue(
    completeDeliveryId(deliveryQuery),
    300
  )

  const summary = useWebhookSummary(windowDays)
  const attempts = useWebhookAttempts({ windowDays, status, deliveryExternalId })

  return (
    <>
      <DeadEndpointNotice resource={summary} />
      <SummaryStats resource={summary} />
      <EndpointsCard
        canEdit={canEdit}
        onSaved={config.refresh}
        resource={deliveryEndpoints(config.resource)}
        type="deliveries"
      />
      <AttemptsCard
        deliveryExternalId={deliveryExternalId}
        deliveryQuery={deliveryQuery}
        isLoadingMore={attempts.isLoadingMore}
        loadMore={attempts.loadMore}
        loadMoreFailed={attempts.loadMoreFailed}
        nextCursor={attempts.nextCursor}
        onDeliveryQueryChange={setDeliveryQuery}
        onStatusChange={setStatus}
        onWindowChange={onWindowChange}
        resource={attempts.resource}
        status={status}
        windowDays={windowDays}
      />
    </>
  )
}
